"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Quote, Sparkles } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const narrativeBlocks = [{
  title: "A Foundation of Transparency",
  body: "At Premier Home Loans, our legacy is built on the bedrock of absolute transparency. We believe that every homeowner deserves a clear, unobstructed view of their financial future. By stripping away the complexity of traditional lending, we provide a straightforward path to ownership that honors your trust and secures your family's generational wealth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686228/img/mortgage_broker_team_portrait.png",
  alt: "Professional mortgage team in a gold-framed portrait",
  quote: "Transparency isn't just a policy; it's the cornerstone of every home we help build and every legacy we help secure.",
  reverse: false
}, {
  title: "The Gilded Standard of Service",
  body: "Our mission is to redefine the mortgage experience through precision and prestige. We don't just process loans; we architect financial solutions that stand the test of time. From first-time buyers to seasoned investors, our commitment to excellence ensures that your journey to the front door is as seamless as it is rewarding.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686228/img/luxury_home_keys_gold.png",
  alt: "Gold keys representing the standard of excellence in lending",
  quote: "We measure our success not by the volume of loans closed, but by the strength of the foundations we help our clients lay.",
  reverse: true
}];
function RevealWrapper({
  children,
  className,
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", className)} style={{
    transitionDelay: `${delay}ms`
  }}>
      {children}
    </div>;
}
export default function OurMission() {
  return <section data-section-id="234013" id="our-mission" className="relative w-full bg-background text-foreground py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-primary/10 rounded-md blur-3xl mix-blend-multiply dark:mix-blend-screen" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-secondary/20 rounded-md blur-3xl mix-blend-multiply dark:mix-blend-screen" />
      </div>

      <div className="container mx-auto px-6 md:px-12 max-w-6xl">
        {}
        <RevealWrapper className="flex flex-col items-center text-center mb-24">
          <div className="inline-flex items-center justify-center p-3 bg-secondary/20 rounded-md mb-6 text-secondary">
            <Sparkles className="w-6 h-6" />
          </div>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-6 text-primary">
            Our Mission
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl font-default leading-relaxed">
            Setting the gilded standard in mortgage brokerage through unwavering transparency, precision, and a commitment to your legacy.
          </p>
        </RevealWrapper>

        {}
        <div className="flex flex-col gap-32">
          {narrativeBlocks.map((block, index) => <div key={index} data-index={index} className="flex flex-col gap-24">
              {}
              <div className={cn("grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center")}>
                <RevealWrapper className={cn("order-2", block.reverse ? "lg:order-1" : "lg:order-2")}>
                  <div className="flex flex-col gap-6">
                    <h3 className="font-default text-3xl md:text-4xl font-semibold text-primary">
                      {block.title}
                    </h3>
                    <Separator className="w-12 h-1 bg-secondary rounded-md" />
                    <p className="text-lg leading-relaxed text-muted-foreground font-default">
                      <span className="float-left text-6xl md:text-7xl font-serif font-bold text-secondary mr-4 mt-2 leading-none">
                        {block.body.charAt(0)}
                      </span>
                      {block.body.slice(1)}
                    </p>
                  </div>
                </RevealWrapper>

                <RevealWrapper delay={200} className={cn("order-1", block.reverse ? "lg:order-2" : "lg:order-1")}>
                  <div className="relative group">
                    <div className="absolute -inset-4 bg-secondary/10 rounded-md blur-xl opacity-50 group-hover:opacity-70 transition-opacity duration-700" />
                    <AspectRatio ratio={4 / 5} className="relative z-10">
                      <img src={block.image} alt={block.alt} className="object-cover w-full h-full rounded-md shadow-2xl grayscale hover:grayscale-0 transition-all duration-700 border-4 border-secondary/30" />
                      <div className="absolute inset-0 rounded-md ring-1 ring-inset ring-primary/20 pointer-events-none" />
                    </AspectRatio>
                  </div>
                </RevealWrapper>
              </div>

              {}
              <RevealWrapper delay={100} className="w-full max-w-4xl mx-auto text-center px-4">
                <Quote className="w-12 h-12 mx-auto text-secondary/40 mb-8 rotate-180" />
                <blockquote className="font-serif italic text-3xl md:text-4xl lg:text-5xl leading-tight text-primary/90">
                  "{block.quote}"
                </blockquote>
                <Separator className="w-24 mx-auto mt-12 bg-secondary/30" />
              </RevealWrapper>
            </div>)}
        </div>

        {}
        <RevealWrapper delay={300} className="mt-32 flex flex-col items-center text-center">
          <h3 className="font-default text-2xl md:text-3xl font-semibold mb-8 text-primary">
            Ready to secure your financial legacy?
          </h3>
          <Button asChild size="lg" className="rounded-md px-10 py-7 text-lg font-semibold shadow-xl bg-gradient-to-r from-secondary to-secondary/80 text-primary-foreground hover:shadow-secondary/20 transition-all duration-300">
            <Link to="/ContactPage">
              Apply Now
            </Link>
          </Button>
        </RevealWrapper>
      </div>
    </section>;
}